import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { Waves, Home, Trophy, Sparkles, ArrowRight, Clock, ShieldCheck, Calendar as CalIcon, CreditCard, AlertTriangle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { parseLocalDate } from "@/lib/date";
import { toast } from "sonner";

const ICONS = { clubhouse: Home, pool: Waves, pool_clubhouse: Sparkles, tennis: Trophy };
const IMAGES = {
  clubhouse: "https://customer-assets-lxgj4vgw.emergentagent.net/job_hoa-connect-8/artifacts/u97vlurr_Pool.png",
  pool: "https://customer-assets-lxgj4vgw.emergentagent.net/job_hoa-connect-8/artifacts/u97vlurr_Pool.png",
  pool_clubhouse: "https://customer-assets-lxgj4vgw.emergentagent.net/job_hoa-connect-8/artifacts/u97vlurr_Pool.png",
  tennis: "https://images.pexels.com/photos/12029162/pexels-photo-12029162.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
};

export default function AmenitiesPage() {
  const [amenities, setAmenities] = useState([]);
  const [events, setEvents] = useState([]);
  const [myProperty, setMyProperty] = useState(null);

  useEffect(() => {
    api.get("/amenities").then(({ data }) => setAmenities(data));
    api.get("/calendar/events", { params: { limit: 30 } }).then(({ data }) => setEvents(data));
    api.get("/portal/my-property").then(({ data }) => setMyProperty(data)).catch(() => setMyProperty(null));
  }, []);

  const outstandingBalance = Number(myProperty?.balance || 0);
  const hasBalanceBlock = outstandingBalance > 0.01;

  // Group events by date
  const eventsByDate = events.reduce((acc, e) => {
    (acc[e.date] = acc[e.date] || []).push(e);
    return acc;
  }, {});

  return (
    <div data-testid="amenities-page" className="max-w-7xl mx-auto px-6 py-16">
      <div className="max-w-2xl mb-12">
        <p className="text-xs tracking-[0.3em] uppercase text-[#C86650] mb-2">Amenities</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#1A2421] mb-4">
          Well-cared-for spaces, thoughtfully shared.
        </h1>
        <p className="text-[#5C6A64] text-base leading-relaxed">
          Reservations are handled through a simple booking request. Select an amenity below to submit a request — the board will review and confirm.
        </p>
      </div>

      {hasBalanceBlock && (
        <div
          data-testid="amenities-balance-ribbon"
          className="mb-6 rounded-2xl border-2 border-[#C86650] bg-[#FDF4F1] p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center gap-3 sm:gap-4"
        >
          <div className="w-9 h-9 rounded-full bg-[#C86650] flex items-center justify-center flex-shrink-0">
            <AlertTriangle className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1">
            <p className="font-serif text-lg text-[#1A2421]">
              Please clear your balance before booking an amenity
            </p>
            <p className="text-sm text-[#5C6A64]">
              Your property has an outstanding balance of{" "}
              <span className="font-serif text-[#C86650]">${outstandingBalance.toFixed(2)}</span>. Once paid, amenity requests will be enabled automatically.
            </p>
          </div>
          <Link to="/payments">
            <Button data-testid="amenities-balance-ribbon-pay" className="rounded-full bg-[#C86650] hover:bg-[#B4553F] text-white h-10 px-5 whitespace-nowrap">
              Pay balance
            </Button>
          </Link>
        </div>
      )}

      {/* Rentals + refundable deposits explainer */}
      <div className="mb-6 p-6 bg-[#EADCC8] rounded-2xl border border-[#D5CFC4]">
        <div className="flex items-start gap-3 mb-3">
          <ShieldCheck className="w-6 h-6 text-[#2C4C3B] flex-shrink-0 mt-1" />
          <div>
            <p className="font-serif text-xl text-[#1A2421] mb-1">Rentals &amp; Refundable Security Deposits</p>
            <p className="text-sm text-[#5C6A64]">
              Every reservation includes a flat <b>$300 refundable security deposit</b> in addition to the rental fee. The deposit is fully refunded after your event once the facility has been left clean and undamaged.
            </p>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <DepositTier label="Clubhouse + Pool Rental" rental={250} deposit={300} amenityKey="pool_clubhouse" />
          <DepositTier label="Clubhouse Only (Sept – April)" rental={150} deposit={300} amenityKey="clubhouse" />
        </div>
      </div>

      {/* Cancellation policy */}
      <div className="mb-12 p-6 bg-[#FDF3F0] rounded-2xl border border-[#E5C4BB]" data-testid="cancellation-policy">
        <div className="flex items-start gap-3">
          <AlertTriangle className="w-6 h-6 text-[#C86650] flex-shrink-0 mt-1" />
          <div>
            <p className="font-serif text-xl text-[#1A2421] mb-1">Cancellation Policy</p>
            <p className="text-sm text-[#5C6A64] leading-relaxed">
              A minimum of one week's advance notice is required for all cancellations. If a cancellation is not received at least <b>seven (7) days</b> prior to the scheduled event, a <b>non-refundable cancellation fee of $150</b> will apply.
            </p>
          </div>
        </div>
      </div>

      {/* Amenity cards */}
      <div className="grid md:grid-cols-2 gap-8 mb-16">
        {amenities.map((a) => {
          const Icon = ICONS[a.key] || Home;
          return (
            <div
              key={a.key}
              data-testid={`amenity-${a.key}`}
              className="rounded-2xl overflow-hidden border border-[#D5CFC4] bg-white hover:-translate-y-1 transition-transform duration-300 hover:shadow-[0_8px_30px_rgba(44,76,59,0.08)]"
            >
              <div className="aspect-[16/10] overflow-hidden">
                <img src={IMAGES[a.key] || IMAGES.clubhouse} alt={a.name} className="w-full h-full object-cover" />
              </div>
              <div className="p-8">
                <div className="flex items-start justify-between mb-4">
                  <div className="w-11 h-11 rounded-full bg-[#EADCC8] flex items-center justify-center">
                    <Icon className="w-5 h-5 text-[#2C4C3B]" />
                  </div>
                  <div className="text-right">
                    <p className="font-serif text-3xl text-[#2C4C3B]">${a.price.toFixed(2)}</p>
                    <p className="text-xs text-[#5C6A64]">rental fee</p>
                  </div>
                </div>
                <h2 className="font-serif text-2xl text-[#1A2421] mb-2">{a.name}</h2>
                {a.description && <p className="text-[#5C6A64] text-sm mb-4 leading-relaxed">{a.description}</p>}
                <div className="flex flex-wrap gap-4 text-xs text-[#1A2421] mb-6">
                  {a.operating_hours && <span className="flex items-center gap-1"><Clock className="w-3 h-3 text-[#C86650]" /> {a.operating_hours}</span>}
                  {a.security_deposit > 0 && <span className="flex items-center gap-1"><ShieldCheck className="w-3 h-3 text-[#C86650]" /> ${a.security_deposit.toFixed(0)} deposit</span>}
                </div>
                <Link to={`/book/${a.key}`}>
                  <Button data-testid={`book-${a.key}-btn`} className="rounded-full bg-[#2C4C3B] hover:bg-[#233D2F] text-white">
                    Request booking <ArrowRight className="ml-2 w-4 h-4" />
                  </Button>
                </Link>
              </div>
            </div>
          );
        })}
      </div>

      {/* Reservation Calendar */}
      <div className="mb-12">
        <div className="flex items-center gap-3 mb-6">
          <CalIcon className="w-6 h-6 text-[#C86650]" />
          <h2 className="font-serif text-3xl text-[#1A2421]">Community Calendar</h2>
        </div>
        <p className="text-[#5C6A64] mb-6">Check confirmed reservations and community events before requesting a date.</p>
        <Card className="rounded-2xl border-[#D5CFC4]" data-testid="reservation-calendar">
          <CardContent className="p-6">
            {events.length === 0 ? (
              <p className="text-center py-8 text-[#5C6A64]">No upcoming reservations or events on the calendar.</p>
            ) : (
              <div className="space-y-4">
                {Object.entries(eventsByDate).map(([date, dayEvents]) => {
                  const d = parseLocalDate(date) || new Date();
                  return (
                  <div key={date} className="flex gap-4 border-b border-[#D5CFC4] pb-3 last:border-0">
                    <div className="w-20 text-center flex-shrink-0">
                      <p className="text-xs uppercase tracking-wider text-[#C86650]">{d.toLocaleString("default", { month: "short" })}</p>
                      <p className="font-serif text-3xl text-[#2C4C3B]">{d.getDate()}</p>
                    </div>
                    <div className="flex-1 space-y-2">
                      {dayEvents.map((e) => (
                        <div key={e.id || `${date}-${e.title}-${e.time_slot || ''}`} className="flex items-start gap-2">
                          <Badge className={e.type === "reservation" ? "bg-[#C86650]" : "bg-[#2C4C3B]"}>{e.type}</Badge>
                          <div className="flex-1">
                            <p className="text-[#1A2421] font-medium">{e.title}</p>
                            {e.time && <p className="text-xs text-[#5C6A64]">{e.time}</p>}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  );
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

function DepositTier({ label, rental, deposit, amenityKey }) {
  return (
    <div className="bg-white rounded-xl p-4 flex flex-col" data-testid={`deposit-tier-${amenityKey}`}>
      <p className="text-xs uppercase tracking-wider text-[#5C6A64]">{label}</p>
      <div className="mt-1 flex items-baseline gap-3 flex-wrap">
        <p className="font-serif text-2xl text-[#2C4C3B]">${rental}</p>
        <span className="text-xs text-[#5C6A64]">rental</span>
        <span className="text-xs text-[#5C6A64]">·</span>
        <p className="font-serif text-lg text-[#C86650]">+ ${deposit}</p>
        <span className="text-xs text-[#5C6A64]">refundable deposit</span>
      </div>
      <Link to={`/book/${amenityKey}`} className="mt-3">
        <Button
          data-testid={`request-booking-${amenityKey}`}
          size="sm"
          className="w-full rounded-full bg-[#C86650] hover:bg-[#B4553F] text-white text-xs"
        >
          Request booking <ArrowRight className="w-3 h-3 ml-1" />
        </Button>
      </Link>
    </div>
  );
}

